// src/pages/BranchDetail.tsx
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import { Button } from '@/components/ui/button';
import { branches } from '@/data/mock/branches';
import { sports } from '@/data/mock/sports';
import { 
  MapPin, 
  Phone, 
  Building2, 
  ArrowLeft,
  Trophy,
  Clock,
  Users,
  Star,
  CheckCircle2,
  Calendar,
  Mail,
  Navigation,
  Dumbbell,
  Wifi,
  Coffee,
  Zap,
  Shield,
  Award,
  Map as MapIcon,
  MessageSquare,
  Send
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useState } from 'react';

const BranchDetail = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [selectedImage, setSelectedImage] = useState(0);

  const branch = branches.find(b => b.slug === slug);

  if (!branch) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-surface-1">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-muted mb-6">
            <Building2 className="h-12 w-12 text-muted-foreground" />
          </div>
          <h1 className="text-3xl font-bold mb-4">{t('notFound.title')}</h1>
          <p className="text-muted-foreground mb-6">
            {isRTL ? 'الفرع غير موجود' : 'Branch not found'}
          </p>
          <Link to="/branches">
            <Button size="lg" className="btn-glow">
              <ArrowLeft className={`h-5 w-5 ${isRTL ? 'rotate-180 ml-2' : 'mr-2'}`} />
              {t('common.back')}
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  const branchSports = sports.filter(s => branch.availableSports.includes(s.id));

  // Mock images gallery - استبدلها بصور حقيقية
  const galleryImages = [
    branch.image,
    branch.image,
    branch.image,
    branch.image,
  ];

  // Build facilities from branch data
  const facilitiesWithIcons = branch.facilities.map((facility) => {
    const en = typeof facility === 'string' ? facility : facility.en;
    const ar = typeof facility === 'string' ? facility : facility.ar;
    return { name: en, ar, icon: CheckCircle2 };
  });

  return (
    <div className="min-h-screen">
      {/* Enhanced Page Hero */}
      <PageHero
        title={branch.name[isRTL ? 'ar' : 'en']}
        subtitle={branch.address[isRTL ? 'ar' : 'en']}
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.branches'), path: '/branches' },
          { label: branch.name[isRTL ? 'ar' : 'en'] }
        ]}
        backgroundImage={branch.image}
      />

      {/* Back Button */}
      <section className="py-8 bg-background border-b border-border">
        <div className="container mx-auto px-4">
          <Link to="/branches">
            <Button variant="ghost" size="lg" className="group">
              <ArrowLeft className={`h-5 w-5 ${isRTL ? 'rotate-180 ml-2' : 'mr-2'} transition-transform group-hover:-translate-x-1`} />
              {isRTL ? 'العودة إلى الفروع' : 'Back to Branches'}
            </Button>
          </Link>
        </div>
      </section>

      {/* Main Branch Badge */}
      {branch.isMainBranch && (
        <section className="py-6 bg-gradient-to-r from-primary via-primary to-accent">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-center gap-3"
            >
              <Star className="h-6 w-6 text-primary-foreground fill-primary-foreground" />
              <span className="text-lg font-bold text-primary-foreground">
                {isRTL ? '⭐ الفرع الرئيسي - الأكبر والأكثر تجهيزاً ⭐' : '⭐ Main Branch - Largest & Most Equipped ⭐'}
              </span>
              <Star className="h-6 w-6 text-primary-foreground fill-primary-foreground" />
            </motion.div>
          </div>
        </section>
      )}

      {/* Image Gallery Section */}
      <section className="py-12 bg-gradient-to-b from-surface-1 to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            {/* Main Image */}
            <div className="relative h-[500px] rounded-3xl overflow-hidden mb-6 border-2 border-border shadow-2xl">
              <motion.img
                key={selectedImage}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                src={galleryImages[selectedImage]}
                alt={branch.name[isRTL ? 'ar' : 'en']}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
              
              {/* Image Counter */}
              <div className="absolute top-6 right-6 px-4 py-2 rounded-full bg-card/90 backdrop-blur-xl border border-border/50 shadow-lg">
                <span className="text-sm font-semibold">
                  {selectedImage + 1} / {galleryImages.length}
                </span>
              </div>
            </div>

            {/* Thumbnail Grid */}
            <div className="grid grid-cols-4 gap-4">
              {galleryImages.map((img, idx) => (
                <motion.button
                  key={idx}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedImage(idx)}
                  className={`relative h-24 rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                    selectedImage === idx 
                      ? 'border-primary shadow-lg shadow-primary/20' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  {selectedImage === idx && (
                    <div className="absolute inset-0 bg-primary/20 backdrop-blur-[1px]" />
                  )}
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Info Cards */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Address */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
              whileHover={{ y: -8 }}
              className="p-8 rounded-2xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500"
            >
              <div className="p-4 rounded-2xl bg-primary/10 border border-primary/20 w-fit mb-4">
                <MapPin className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-bold text-lg mb-2">{t('branches.address')}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {branch.address[isRTL ? 'ar' : 'en']}
              </p>
              <Button variant="link" className="px-0 mt-3 text-primary">
                <Navigation className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'احصل على الاتجاهات' : 'Get Directions'}
              </Button>
            </motion.div>

            {/* Phone */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              whileHover={{ y: -8 }}
              className="p-8 rounded-2xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500"
            >
              <div className="p-4 rounded-2xl bg-primary/10 border border-primary/20 w-fit mb-4">
                <Phone className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-bold text-lg mb-2">{t('branches.phone')}</h3>
              <a 
                href={`tel:${branch.phone}`} 
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {branch.phone}
              </a>
              <Button variant="link" className="px-0 mt-3 text-primary block">
                <MessageSquare className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'أرسل رسالة' : 'Send Message'}
              </Button>
            </motion.div>

            {/* Hours */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              whileHover={{ y: -8 }}
              className="p-8 rounded-2xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500"
            >
              <div className="p-4 rounded-2xl bg-green-500/10 border border-green-500/20 w-fit mb-4">
                <Clock className="h-8 w-8 text-green-500" />
              </div>
              <h3 className="font-bold text-lg mb-2">
                {isRTL ? 'ساعات العمل' : 'Working Hours'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {isRTL ? 'السبت - الخميس' : 'Sat - Thu'}
              </p>
              <p className="text-sm font-semibold text-foreground mt-1">
                8:00 AM - 8:00 PM
              </p>
              <div className="flex items-center gap-2 mt-3">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-xs font-semibold text-green-500">
                  {isRTL ? 'مفتوح الآن' : 'Open Now'}
                </span>
              </div>
            </motion.div>

            {/* Facilities Count */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              whileHover={{ y: -8 }}
              className="p-8 rounded-2xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500"
            >
              <div className="p-4 rounded-2xl bg-primary/10 border border-primary/20 w-fit mb-4">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-bold text-lg mb-2">
                {isRTL ? 'المرافق' : 'Facilities'}
              </h3>
              <p className="text-4xl font-bold gradient-text mb-2">
                {branch.facilities.length}
              </p>
              <p className="text-sm text-muted-foreground">
                {isRTL ? 'مرفق متاح' : 'Available Facilities'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section className="py-20 bg-gradient-to-b from-surface-1 to-background relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Zap className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'المرافق المتاحة' : 'Available Facilities'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'مرافق عالمية المستوى' : 'World-Class Facilities'}
            </h2>

            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {isRTL
                ? 'نوفر أفضل المرافق الرياضية لضمان تجربة تدريبية مثالية'
                : 'We provide the best sports facilities to ensure a perfect training experience'
              }
            </p>
          </motion.div>

          {/* Facilities Grid */}
          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
            {facilitiesWithIcons.map((facility, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ scale: 1.05, y: -4 }}
                className="p-6 rounded-2xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 border border-primary/20 mb-4">
                  <facility.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-semibold text-sm">
                  {isRTL ? facility.ar : facility.name}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Available Sports Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
                <Trophy className="h-5 w-5 text-primary" />
                <span className="text-sm font-semibold text-primary">
                  {t('branches.availableSports')}
                </span>
              </div>

              <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
                {isRTL ? 'الرياضات المتاحة' : 'Available Sports'}
              </h2>

              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {isRTL
                  ? `اختر من بين ${branchSports.length} رياضة متاحة في هذا الفرع`
                  : `Choose from ${branchSports.length} sports available at this branch`
                }
              </p>
            </motion.div>

            {/* Sports Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {branchSports.map((sport, idx) => (
                <motion.div
                  key={sport.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ y: -4 }}
                >
                  <Link to={`/sports/${sport.slug}`} className="group block">
                    <div className="p-6 rounded-2xl bg-card border border-border hover:border-primary/50 shadow-xl hover:shadow-2xl hover:shadow-primary/10 transition-all duration-500">
                      <div className="flex items-center gap-4">
                        {/* Icon */}
                        <motion.div
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.6 }}
                          className="flex-shrink-0 w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center group-hover:bg-primary/20 transition-colors"
                        >
                          <Trophy className="h-8 w-8 text-primary" />
                        </motion.div>

                        {/* Content */}
                        <div className="flex-1">
                          <h3 className="font-bold text-lg mb-1 group-hover:text-primary transition-colors">
                            {sport.name[isRTL ? 'ar' : 'en']}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1.5">
                              <Users className="h-4 w-4" />
                              <span>{sport.coaches} {isRTL ? 'مدرب' : 'Coaches'}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                              <span className="text-green-500">{isRTL ? 'متاح' : 'Available'}</span>
                            </div>
                          </div>
                        </div>

                        {/* Arrow */}
                        <motion.div
                          whileHover={{ x: isRTL ? -4 : 4 }}
                          className="flex-shrink-0"
                        >
                          <ArrowLeft className={`h-5 w-5 text-primary ${isRTL ? '' : 'rotate-180'}`} />
                        </motion.div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gradient-to-b from-surface-1 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
                <MapIcon className="h-5 w-5 text-primary" />
                <span className="text-sm font-semibold text-primary">
                  {isRTL ? 'الموقع' : 'Location'}
                </span>
              </div>

              <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
                {isRTL ? 'احصل على الاتجاهات' : 'Get Directions'}
              </h2>
            </motion.div>

            {/* Map Placeholder */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="aspect-video rounded-3xl overflow-hidden border-2 border-border shadow-2xl"
            >
              <div className="w-full h-full bg-muted flex items-center justify-center">
                <div className="text-center">
                  <MapIcon className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground font-medium mb-4">
                    {isRTL ? 'خريطة تفاعلية' : 'Interactive Map'}
                  </p>
                  <Button variant="outline">
                    <Navigation className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {isRTL ? 'فتح في خرائط جوجل' : 'Open in Google Maps'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-background relative overflow-hidden">
        {/* Animated Background */}
        <motion.div
          animate={{
            backgroundPosition: ["0% 50%", "100% 50%"],
          }}
          transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 bg-[length:200%_100%]"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Award className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'ابدأ الآن' : 'Start Now'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 gradient-text">
              {isRTL ? 'جاهز للانضمام؟' : 'Ready to Join?'}
            </h2>

            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              {isRTL
                ? 'احجز جلستك التجريبية المجانية اليوم وابدأ رحلتك الرياضية معنا'
                : 'Book your free trial session today and start your sports journey with us'
              }
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking">
                <Button size="lg" className="btn-glow px-12 py-7 text-lg rounded-full">
                  <Calendar className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'احجز الآن' : 'Book Now'}
                </Button>
              </Link>

              <Button size="lg" variant="outline" className="px-12 py-7 text-lg rounded-full border-2">
                <Phone className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'اتصل بنا' : 'Call Us'}
              </Button>
            </div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap justify-center gap-6 mt-12 pt-12 border-t border-border"
            >
              {[
                { icon: CheckCircle2, text: isRTL ? 'جلسة تجريبية مجانية' : 'Free Trial Session' },
                { icon: Shield, text: isRTL ? 'بيئة آمنة' : 'Safe Environment' },
                { icon: Award, text: isRTL ? 'مدربون معتمدون' : 'Certified Coaches' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-muted-foreground">
                  <item.icon className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BranchDetail;
